import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { API_ENDPOINTS } from '@caro/shared/constants';
import type { UserPhoto } from '@caro/shared/types';

import { DEFAULT_BACKGROUND_URL } from '../constants/defaults';

const SLIDE_INTERVAL = 30_000; // 30 seconds
const CROSSFADE_DURATION = 1_000; // 1 second

export function PhotoFrame() {
  const { data: photos } = useApi<UserPhoto[]>(API_ENDPOINTS.USER_PHOTOS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!photos || photos.length <= 1) return;

    timerRef.current = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % photos.length);
        setIsTransitioning(false);
      }, CROSSFADE_DURATION);
    }, SLIDE_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [photos]);

  // Fall back to the stock background when the resident has no photos so the
  // home screen always has a backdrop (was: render nothing).
  const hasPhotos = Array.isArray(photos) && photos.length > 0;
  const safeIndex = hasPhotos && currentIndex < photos!.length ? currentIndex : 0;
  const currentPhoto = hasPhotos ? photos![safeIndex] : null;
  const isDefaultBackground = !hasPhotos;
  const displayUrl = currentPhoto?.url || DEFAULT_BACKGROUND_URL;

  // Overlay strength: real resident photos can be any brightness, so they keep
  // a moderate darkening for white-text legibility. The stock default
  // background is designed to sit behind the UI, so it gets only a very slight
  // wash so it stays bright and on-brand.
  // Under a resident theme the overlay is the theme's scrim (Editorial: an aubergine composition
  // over the stock image, an aubergine-tinted darkening over resident photos); without one these
  // fall back to exactly the gradients above.
  const overlayGradient = isDefaultBackground
    ? 'var(--caro-home-scrim, linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.08) 60%, rgba(0,0,0,0.16) 100%))'
    : 'var(--caro-home-scrim-photo, linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.32) 40%, rgba(0,0,0,0.48) 100%))';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        overflow: 'hidden',
      }}
    >
      <img
        src={displayUrl}
        alt={currentPhoto?.description || 'Photo'}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: isTransitioning ? 0 : 1,
          transition: `opacity ${CROSSFADE_DURATION}ms ease-in-out`,
        }}
      />
      {/* CARO logo — bottom-left, BEHIND the overlay (rendered before it so the
       *  overlay paints on top and dims it slightly). Absolutely positioned
       *  within the fixed full-screen frame, so it stays anchored to the
       *  bottom-left edge of the screen on any display size and in both
       *  landscape and portrait. Size scales gently with the viewport. */}
      <img
        src="https://caro.care/images/logo/Caro_logo_low.png"
        alt=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: '24px',
          left: '24px',
          height: 'clamp(40px, 8vh, 88px)',
          width: 'auto',
          opacity: 0.9,
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))',
          pointerEvents: 'none',
        }}
      />
      {/* Overlay for readability. Strength depends on the background (see
       *  overlayGradient): a light wash for the default image, moderate
       *  darkening for resident photos. */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: overlayGradient,
        }}
      />
      {/* Photo metadata */}
      {currentPhoto?.description && (
        <div
          style={{
            position: 'absolute',
            bottom: '100px',
            left: '24px',
            right: '24px',
            padding: '8px 12px',
            backgroundColor: 'var(--caro-home-caption-bg, rgba(0,0,0,0.5))',
            borderRadius: '8px',
            color: 'white',
            fontSize: 'var(--font-body)',
          }}
        >
          {currentPhoto.description}
          {currentPhoto.uploaderName && (
            <span style={{ opacity: 0.7, marginLeft: '8px' }}>
              — {currentPhoto.uploaderName}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
